import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import type { PromoItemParams, PromoItemSearch } from '../types/promotion.types';
import {
  toPromoCatalogueFilterSearch,
  toPromoCatalogueFilterValues,
} from '../utils/promotionFilters';

const ROUTES = {
  items: getRouteApi('/_auth/promotion/items'),
  itemTypes: getRouteApi('/_auth/promotion/item-types'),
} as const;

export type PromoCatalogueRouteId = keyof typeof ROUTES;

const normalizeFilterValues = (values: Record<string, string>) =>
  toPromoCatalogueFilterValues(toPromoCatalogueFilterSearch(values));

export function usePromoCatalogueFilters(routeId: PromoCatalogueRouteId) {
  const route = ROUTES[routeId];
  const search: PromoItemSearch = route.useSearch();
  const navigate = route.useNavigate();

  const params = useMemo<PromoItemParams>(
    () => ({
      search: search.search,
      itemTypeId: search.itemTypeId,
      itemCode: search.itemCode,
      bookEditionNo: search.bookEditionNo,
      ...(search.activeState === 'all' ? {} : { isActive: search.activeState === 'active' }),
    }),
    [search],
  );

  const appliedValues = useMemo(() => toPromoCatalogueFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    normalizeValues: normalizeFilterValues,
    shouldWaitForApply: true,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toPromoCatalogueFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const nameSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...nameSearch, search, params };
}
